import React from 'react';

import HeartsIllustration from '../../../assets/svg-components/support/hearts.svg';
import FirstCardIllustration from '../../../assets/svg-components/support/pink_section_card_01_01.svg';
import SatisfactionThumbIllustration from '../../../assets/svg-components/support/satisfaction_section_thumb.svg';
import { Card } from '../../../components/card/card';
import CircularProgress from '../../../components/circular-progress/circlular-progress';
import { SectionContent } from '../../../components/section/section-content';
import { SectionHero } from '../../../components/section/section-hero';
import { SectionTitle } from '../../../components/section/section-title';
import { SvgWithGenericAnimation } from '../../../components/svg-with-generic-animation/svg-with-generic-animation';
import { Text } from '../../../components/text/text';
import { Title } from '../../../components/title/title';
import { Translate } from '../../../components/translate';
import { MessageBubbleSvg } from './message-bubble-svg';
import SupportCardComponent from './support-card';
import SupportInteractionsSVG from './support-interactions-svg';
import './support.scss';

interface SupportSectionProps {
  average_resolution_time?: number;
  asked_questions?: number;
  chat_interactions: number;
}

const SITEGROUND_AVERAGE_RESOLUTION_TIME = 15;

export const SupportSection: React.FC<SupportSectionProps> = ({
  average_resolution_time: customer_resolution_time,
  asked_questions,
  chat_interactions
}) => {
  const checkClientInteractionsAndAverageResolutionTime = chat_interactions && customer_resolution_time < 15;

  return (
    <React.Fragment>
      <SectionHero className="rc-support-section">
        <HeartsIllustration className="rc-support-section-heading__pink-hearts" />
        <img src="https://www.siteground.com/static/en/img/recap2023/06_BG.png" alt="support section image" />

        <SectionTitle>
          <Translate id="translate.support.section.title" />
        </SectionTitle>
      </SectionHero>
      <SectionContent className="rc-support-section">
        {checkClientInteractionsAndAverageResolutionTime && (
          <SupportCardComponent
            icon={<FirstCardIllustration width="100%" />}
            title={
              <Translate
                id="translate.support.section.card.average.resolution.title"
                values={{ average_resolution_time: SITEGROUND_AVERAGE_RESOLUTION_TIME - customer_resolution_time }}
              />
            }
            text={
              <Translate
                id="translate.support.section.card.average.resolution.text"
                values={{ chat_interactions: chat_interactions, avg_resolution_time: customer_resolution_time }}
              />
            }
          />
        )}
        <SupportCardComponent
          icon={
            <CircularProgress
              animationSpeed={65}
              value={24.5}
              animationName="support-card-animation"
              unfilledColor="#d68af3"
              filledColor="#ffe766"
            >
              <MessageBubbleSvg width="100%" />
            </CircularProgress>
          }
          title={<Translate id="translate.support.section.card.phone.title" />}
          reverted
          text={<Translate id="translate.support.section.card.phone.text" />}
        />
        <SupportCardComponent
          icon={<SupportInteractionsSVG width="100%" />}
          title={<Translate id="translate.support.section.card.customer.support.title" />}
          text={<Translate id="translate.support.section.card.customer.support.text" />}
        />

        <SupportCardComponent
          className="rc-support-section-second-card"
          icon={<SvgWithGenericAnimation name="ai" />}
          title={
            asked_questions ? (
              <Translate id="translate.support.section.card.ai.title" values={{ number: asked_questions }} />
            ) : (
              <Translate id="translate.support.section.card.alternative.ai.title" />
            )
          }
          text={
            asked_questions ? (
              <Translate id="translate.support.section.card.ai.text" />
            ) : (
              <Translate id="translate.support.section.card.alternative.ai.text" />
            )
          }
          reverted
          afterContent={<HeartsIllustration className="rc-support-section-second-card__pink-hearts" />}
        />

        <SupportCardComponent
          icon={<SvgWithGenericAnimation name="meet-offline" />}
          title={<Translate id="translate.support.section.card.meet.title" />}
          text={<Translate id="translate.support.section.card.meet.text" />}
        />

        <SupportCardComponent
          icon={<SvgWithGenericAnimation name="winner-cup" />}
          title={<Translate id="translate.support.section.card.awards.title" />}
          text={<Translate id="translate.support.section.card.awards.text" />}
          reverted
        />
        <Card
          column
          className="rc-support-card rc-support-satisfaction-card"
          content={
            <div className="rc-support-satisfaction-card__content">
              <Title level="3" weight="bold" align="center">
                <Translate id="translate.support.section.card.satisfaction.title" />
              </Title>

              <Text size="large" align="center">
                <Translate id="translate.support.section.card.satisfaction.text" />
              </Text>
            </div>
          }
          icon={
            <CircularProgress
              filledColor="#79CC95"
              unfilledColor="rgba(121, 204, 149, 0.1)"
              animationName="rc-support-satisfaction-card--animated"
              value={96}
              valueSuffix="%"
            >
              <SatisfactionThumbIllustration width="200px" />
            </CircularProgress>
          }
          afterContent={
            <React.Fragment>
              <HeartsIllustration className="rc-support-section-satisfaction-card__pink-hearts" />
              <HeartsIllustration className="rc-support-section-satisfaction-card__green-hearts" />
            </React.Fragment>
          }
        />
      </SectionContent>
    </React.Fragment>
  );
};
